import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { useD3Tooltip } from '../hooks/useD3Tooltip';
import { useD3Chart } from '../hooks/useD3Chart';
import { useD3Zoom } from '../hooks/useD3Zoom';
import { movingAverageIntensity, determineWindowSize } from '../utils/movingAverage';
import * as styles from '../styles/components/histogram.css.ts';
import { COLORS } from '../constants/colors';
import logger from '../utils/logger';

/**
 * DateDistribution - Datetime型データの分布可視化コンポーネント
 *
 * 特徴:
 * - Rug plot: 個別イベントを時間軸上に縦線として表示
 * - KDE (Kernel Density Estimation): 滑らかな密度曲線
 * - 時間軸のフォーマット: 自動的に適切なフォーマットを選択
 *
 * データの扱い:
 * - Date型のデータをUnix timestamp（ミリ秒）に変換して処理
 * - 軸の表示は日付フォーマット
 */

interface DateDistributionProps {
  /** Date型の配列 */
  data: Date[];
  /** SVG幅（デフォルト: 340） */
  width?: number;
  /** SVG高さ（デフォルト: 220） */
  height?: number;
  /** 密度推定の帯域幅（自動計算の場合は未指定） */
  densityBandwidth?: number;
  /** 密度推定を表示するか */
  showDensity?: boolean;
  /** 元データのインデックス情報（クリックイベント用） */
  originalData?: { value: Date; index: number }[];
  /** 選択された行インデックス */
  selectedIndices?: number[];
  /** Rugプロットのtickをクリックしたときのコールバック */
  onTickClick?: (dataIndices: number[], isAdditive?: boolean) => void;
}

export function RugPlot({
  data,
  width: defaultWidth = 340,
  height,
  densityBandwidth,
  showDensity = true,
  originalData,
  selectedIndices = [],
  onTickClick
}: DateDistributionProps) {
  const tooltip = useD3Tooltip({
    className: 'date-distribution-tooltip',
    style: {
      padding: '8px',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      borderRadius: '4px',
      fontSize: '12px',
      pointerEvents: 'none' as const,
      whiteSpace: 'nowrap' as const,
      zIndex: 1000
    }
  });

  const margin = useMemo(() => ({ top: 20, right: 20, bottom: 40, left: 60 }), []);

  // 使用する高さを決定: propsのheight、それがなければ220
  const effectiveHeight = height ?? 220;

  // モード管理（pan or select）
  const [mode, setMode] = useState<'pan' | 'select'>('pan');

  // 現在のズームトランスフォームを保持
  const [currentTransform, setCurrentTransform] = useState<d3.ZoomTransform>(d3.zoomIdentity);

  // ブラシの状態を保持
  const brushRef = useRef<d3.BrushBehavior<unknown> | null>(null);
  const brushGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const isClearingRef = useRef(false);

  // 元のスケールを保持（ズーム前）
  const originalScaleRef = useRef<d3.ScaleTime<number, number> | null>(null);

  // DateをUnix timestampに変換
  const numericData = useMemo(() => {
    return data.map(d => d.getTime());
  }, [data]);

  // 時間範囲を計算
  const timeExtent = useMemo(() => {
    return d3.extent(data) as [Date, Date];
  }, [data]);

  const { containerRef, svgRef } = useD3Chart<{
    xScale: d3.ScaleTime<number, number>;
    yScale: d3.ScaleLinear<number, number>;
    xAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    yAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    yAxisLabel: d3.Selection<SVGTextElement, unknown, null, undefined>;
    windowSizeLabel: d3.Selection<SVGTextElement, unknown, null, undefined>;
    rugGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
    densityGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
    brushGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
  }>({
    defaultWidth,
    defaultHeight: effectiveHeight,
    monitorWidth: true,
    monitorHeight: true,
    margin,
    setup: ({ svg, g, innerWidth, innerHeight }) => {
      // クリップパスを作成（Y軸より右側全域を描画領域とする）
      const clipId = `clip-date-${Math.random().toString(36).substring(2, 11)}`;
      svg.append('defs')
        .append('clipPath')
        .attr('id', clipId)
        .append('rect')
        .attr('x', 0) // Y軸の位置から
        .attr('y', -100) // 上に余裕を持たせる
        .attr('width', 10000) // 右側全域（十分大きな値）
        .attr('height', innerHeight + 300); // 下にも余裕を持たせる（rug plot + ストローク幅を考慮）

      // Rugプロット用のグループ（clipPath適用）
      const rugGroup = g.append('g')
        .attr('class', 'rug-plot')
        .attr('clip-path', `url(#${clipId})`);

      // 密度曲線用のグループ（clipPath適用）
      const densityGroup = g.append('g')
        .attr('class', 'density-curve')
        .attr('clip-path', `url(#${clipId})`);

      // ブラシ用のグループ
      const brushGroup = g.append('g').attr('class', 'brush');

      // X軸グループを作成
      const xAxis = g.append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${innerHeight})`);

      // Y軸グループを作成
      const yAxis = g.append('g')
        .attr('class', 'y-axis');

      // Y軸ラベル
      const yAxisLabel = g.append('text')
        .attr('class', 'y-axis-label')
        .attr('transform', 'rotate(-90)')
        .attr('y', -48) // tickと数値から十分離す
        .attr('x', -innerHeight / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '9px')
        .style('fill', '#666');

      // 移動平均のウィンドウサイズ情報
      const windowSizeLabel = g.append('text')
        .attr('class', 'window-size-label')
        .attr('x', innerWidth)
        .attr('y', -5)
        .attr('text-anchor', 'end')
        .style('font-size', '9px')
        .style('fill', '#666')
        .style('font-style', 'italic');

      // スケールを初期化
      const xScale = d3.scaleTime().range([0, innerWidth]);
      const yScale = d3.scaleLinear().range([innerHeight, 0]);

      // 元のスケールを保存
      originalScaleRef.current = xScale.copy();

      return { xScale, yScale, xAxis, yAxis, yAxisLabel, windowSizeLabel, rugGroup, densityGroup, brushGroup };
    },
    render: ({ refs, innerWidth, innerHeight }) => {
      const { xScale, yScale, xAxis, yAxis, yAxisLabel, windowSizeLabel, rugGroup, densityGroup } = refs;
      if (data.length === 0) return;

      // スケールのrangeを更新（リサイズ対応）
      xScale.range([0, innerWidth]);
      yScale.range([innerHeight, 0]);

      // X軸の位置を更新（リサイズ対応）
      xAxis.attr('transform', `translate(0,${innerHeight})`);

      // スケールのドメインを設定
      xScale.domain(timeExtent);

      // 元のスケール（ズーム前）を保存・更新
      if (!originalScaleRef.current || originalScaleRef.current.domain()[0].getTime() !== timeExtent[0].getTime()) {
        originalScaleRef.current = xScale.copy();
      } else {
        // rangeのみ更新
        originalScaleRef.current.range([0, innerWidth]);
      }

      // ズーム適用後のスケール
      const visibleXScale = currentTransform.rescaleX(xScale);

      // 密度推定を計算して強度（intensity）に変換
      let densityData: [number, number][] = [];
      let maxDensity = 0;
      let intensityUnit = 'events/hour';

      if (showDensity && numericData.length > 0) {
        const timeRange = timeExtent[1].getTime() - timeExtent[0].getTime();
        const oneHour = 60 * 60 * 1000;
        const oneDay = 24 * oneHour;

        // 時間範囲に応じてウィンドウサイズと単位を決定
        const windowSize = determineWindowSize(timeRange);

        // 評価点を生成（100点）
        const xValues = d3.range(
          timeExtent[0].getTime(),
          timeExtent[1].getTime(),
          (timeExtent[1].getTime() - timeExtent[0].getTime()) / 100
        );

        // 移動平均ベースで強度を計算（ミリ秒あたり）
        const intensityData = movingAverageIntensity(numericData, windowSize, xValues);

        // 時間範囲に応じて単位を決定し、表示用にスケール
        if (timeRange < 7 * oneDay) {
          // 1週間未満: events/hour
          intensityUnit = 'events/hour';
          densityData = intensityData.map(([x, intensity]) => [x, intensity * oneHour]);
        } else {
          // 1週間以上: events/day
          intensityUnit = 'events/day';
          densityData = intensityData.map(([x, intensity]) => [x, intensity * oneDay]);
        }

        maxDensity = d3.max(densityData, d => d[1]) || 0;
      }

      // Y軸のドメインを設定（強度の最大値に基づく）
      yScale.domain([0, maxDensity * 1.1]);

      // X軸を更新（時間範囲に応じて自動フォーマット）
      const timeRange = timeExtent[1].getTime() - timeExtent[0].getTime();
      const oneDay = 24 * 60 * 60 * 1000;
      const oneYear = 365 * oneDay;

      let tickFormat: (date: Date) => string;
      if (timeRange < oneDay) {
        // 1日未満: 時:分
        tickFormat = d3.timeFormat('%H:%M');
      } else if (timeRange < 7 * oneDay) {
        // 1週間未満: 月/日 時:分
        tickFormat = d3.timeFormat('%m/%d %H:%M');
      } else if (timeRange < oneYear) {
        // 1年未満: 月/日
        tickFormat = d3.timeFormat('%Y/%m/%d');
      } else {
        // 1年以上: 年/月
        tickFormat = d3.timeFormat('%Y/%m');
      }

      // widthに応じてtick数を調整（重ならないように）
      // 1tickあたり約120px確保（ラベルが重ならないように）
      const tickCount = Math.max(2, Math.min(10, Math.floor(innerWidth / 120)));

      xAxis.call(
        d3.axisBottom(visibleXScale)
          .ticks(tickCount)
          .tickFormat(tickFormat as (domainValue: Date | d3.NumberValue, index: number) => string)
      )
        .selectAll('text')
        .style('font-size', '11px')
        .style('fill', '#666');

      xAxis.selectAll('path, line')
        .style('stroke', '#ccc');

      // Y軸を更新（密度値を科学的記数法または適切な桁数で表示）
      yAxis.call(
        d3.axisLeft(yScale)
          .ticks(5)
          .tickFormat(d => {
            const value = d as number;
            // 値が0.01より小さい場合は科学的記数法、それ以外は小数点2桁
            if (Math.abs(value) < 0.01 && value !== 0) {
              return value.toExponential(1);
            }
            return value.toFixed(2);
          })
      )
        .selectAll('text')
        .style('font-size', '11px')
        .style('fill', '#666');

      yAxis.selectAll('path, line')
        .style('stroke', '#ccc');

      // Y軸ラベルを更新（強度の単位を表示）
      yAxisLabel
        .attr('x', -innerHeight / 2)
        .text(`Intensity (${intensityUnit})`);

      // ウィンドウサイズ情報を更新（移動平均の説明）
      if (showDensity && numericData.length > 0) {
        const timeRange = timeExtent[1].getTime() - timeExtent[0].getTime();
        const windowSize = determineWindowSize(timeRange);
        const oneHour = 60 * 60 * 1000;
        const oneDay = 24 * oneHour;

        let windowText = '';
        if (windowSize < oneDay) {
          const hours = Math.round(windowSize / oneHour);
          windowText = `${hours}-hour moving avg`;
        } else {
          const days = Math.round(windowSize / oneDay);
          windowText = `${days}-day moving avg`;
        }

        windowSizeLabel
          .attr('x', innerWidth)
          .text(windowText);
      } else {
        windowSizeLabel.text('');
      }

      // Rugプロットを描画
      const rugHeight = 10;
      const rugY = innerHeight;

      // 選択されているインデックスのSetを作成
      const selectedSet = new Set(selectedIndices);

      // データ量が多い場合は警告
      if (data.length > 50000) {
        logger.warn('[RugPlot] Large dataset detected', { dataPoints: data.length });
      }

      // 大量データから切り替わる場合は古い要素を一括削除（パフォーマンス改善）
      const existingTicks = rugGroup.selectAll('.rug-tick').nodes();
      if (existingTicks.length > 10000) {
        logger.info('[RugPlot] Clearing large number of existing elements', { count: existingTicks.length });
        rugGroup.selectAll('.rug-tick').remove();
      }

      // Rugプロットのtickを描画
      const ticks = rugGroup.selectAll<SVGLineElement, Date>('.rug-tick')
        .data(data, (d: Date) => d.getTime().toString());

      ticks.exit().remove();

      const ticksEnter = ticks.enter()
        .append('line')
        .attr('class', 'rug-tick');

      ticks.merge(ticksEnter)
        .attr('x1', d => visibleXScale(d))
        .attr('x2', d => visibleXScale(d))
        .attr('y1', rugY)
        .attr('y2', rugY - rugHeight)
        .attr('stroke', (_, i) => selectedSet.has(originalData?.[i]?.index ?? i) ? '#e74c3c' : '#3498db')
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.6)
        .style('cursor', onTickClick ? 'pointer' : 'default')
        .on('mouseover', function(event, d) {
          d3.select(this).attr('opacity', 1).attr('stroke-width', 2);
          tooltip.show(
            `${d.toLocaleString()}`,
            event
          );
        })
        .on('mouseout', function() {
          d3.select(this).attr('opacity', 0.6).attr('stroke-width', 1.5);
          tooltip.hide();
        })
        .on('click', function(event, d) {
          if (!onTickClick || !originalData) return;

          const index = data.indexOf(d);
          const dataIndex = originalData[index]?.index ?? index;
          const isAdditive = event.metaKey || event.ctrlKey;

          onTickClick([dataIndex], isAdditive);
        });

      // 密度曲線を描画
      if (showDensity && densityData.length > 0) {
        const line = d3.line<[number, number]>()
          .x(d => visibleXScale(new Date(d[0])))
          .y(d => yScale(d[1]))
          .curve(d3.curveBasis);

        const path = densityGroup.selectAll<SVGPathElement, [number, number][]>('.density-path')
          .data([densityData]);

        path.exit().remove();

        const pathEnter = path.enter()
          .append('path')
          .attr('class', 'density-path');

        path.merge(pathEnter)
          .attr('d', line)
          .attr('fill', 'none')
          .attr('stroke', '#2ecc71')
          .attr('stroke-width', 2)
          .attr('opacity', 0.8);
      } else {
        densityGroup.selectAll('.density-path').remove();
      }
    },
    dependencies: [data, numericData, timeExtent, showDensity, densityBandwidth, selectedIndices, originalData, onTickClick, tooltip, currentTransform]
  });

  // ズーム時の処理
  const handleZoom = useCallback((transform: d3.ZoomTransform) => {
    setCurrentTransform(transform);
  }, []);

  // ズームフィルター（modeに応じて変更）
  const zoomFilter = useCallback((event: WheelEvent | MouseEvent | TouchEvent) => {
    if (mode === 'pan') {
      // パンモード：ホイールはCtrl/Cmd、ドラッグは許可
      if (event.type === 'wheel') {
        return (event as WheelEvent).ctrlKey || (event as WheelEvent).metaKey;
      }
      if (event.type === 'mousedown' || event.type === 'mousemove') {
        return !(event as MouseEvent).button; // 左クリックのみ
      }
      return true;
    } else {
      // 選択モード：ホイールのみ（Ctrl/Cmd必須）
      return event.type === 'wheel' && ((event as WheelEvent).ctrlKey || (event as WheelEvent).metaKey);
    }
  }, [mode]);

  // ズームフックを使用
  // translateExtentでパン範囲を制限（データがある範囲のみ）
  // 座標系はSVG全体（marginを含む）
  useD3Zoom(
    svgRef,
    handleZoom,
    {
      scaleExtent: [1, 10],
      translateExtent: [
        [margin.left, margin.top], // 左上の制限
        [defaultWidth - margin.right, effectiveHeight - margin.bottom] // 右下の制限
      ],
      filter: zoomFilter,
      enableDoubleClickReset: true
    }
  );

  // brushGroupの参照を保持
  const brushGroupElementRef = useRef<SVGGElement | null>(null);

  // ブラシ選択の設定
  useEffect(() => {
    if (mode !== 'select' || !onTickClick) {
      // パンモードまたはコールバックがない場合はブラシをクリア
      if (brushGroupRef.current && brushGroupElementRef.current) {
        brushGroupRef.current.selectAll('*').remove();
        brushGroupRef.current = null;
        brushRef.current = null;
      }
      return;
    }

    // SVGとスケールが初期化されるまで待つ
    const checkAndSetupBrush = () => {
      if (!svgRef.current) return;

      const svg = d3.select(svgRef.current);
      const brushGroup = svg.select<SVGGElement>('g.brush');
      if (brushGroup.empty()) {
        // まだ初期化されていない
        return;
      }

      brushGroupElementRef.current = brushGroup.node();
      brushGroupRef.current = brushGroup;

      const brush = d3.brushX()
        .extent([[0, 0], [defaultWidth - margin.left - margin.right, effectiveHeight - margin.top - margin.bottom]])
        .filter((event: MouseEvent) => {
          return !event.ctrlKey && !event.metaKey;
        })
        .on('start brush end', (event) => {
          if (isClearingRef.current) return;
          if (!event.selection) return;

          const [x0, x1] = event.selection as [number, number];
          if (!originalScaleRef.current) return;

          // ズーム適用後のスケール
          const visibleScale = currentTransform.rescaleX(originalScaleRef.current);

          // 選択範囲の日時を取得
          const date0 = visibleScale.invert(x0);
          const date1 = visibleScale.invert(x1);

          // 範囲内のデータポイントを選択
          const selectedDataIndices: number[] = [];
          data.forEach((d, i) => {
            if (d >= date0 && d <= date1) {
              const originalIndex = originalData?.[i]?.index ?? i;
              selectedDataIndices.push(originalIndex);
            }
          });

          if (event.type === 'end') {
            const isAdditive = event.sourceEvent?.metaKey || event.sourceEvent?.ctrlKey;
            onTickClick(selectedDataIndices, isAdditive);

            // ブラシをクリア
            isClearingRef.current = true;
            brushGroup.call(brush.move, null);
            isClearingRef.current = false;
          }
        });

      brushRef.current = brush;
      brushGroup.call(brush);

      // 選択矩形のスタイル
      brushGroup.select('.selection')
        .style('fill', COLORS.primary.blue)
        .style('fill-opacity', 0.2)
        .style('stroke', COLORS.primary.blue)
        .style('stroke-width', 1);
    };

    // 少し待ってから実行（SVG初期化後）
    const timer = setTimeout(checkAndSetupBrush, 50);

    return () => {
      clearTimeout(timer);
      if (brushGroupRef.current) {
        brushGroupRef.current.selectAll('*').remove();
      }
    };
  }, [mode, margin, defaultWidth, effectiveHeight, onTickClick, data, originalData, currentTransform, svgRef]);

  if (data.length === 0) {
    return (
      <div className={styles.histogramEmpty} style={{ width: defaultWidth, height: effectiveHeight }}>
        No data available
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={styles.histogramContainer}
      style={{ width: '100%', height: '100%', position: 'relative' }}
      onMouseEnter={(e) => {
        const buttons = e.currentTarget.querySelector('.mode-buttons') as HTMLElement;
        if (buttons) buttons.style.opacity = '1';
      }}
      onMouseLeave={(e) => {
        const buttons = e.currentTarget.querySelector('.mode-buttons') as HTMLElement;
        if (buttons) buttons.style.opacity = '0';
      }}
    >
      <svg
        ref={svgRef}
        className={styles.histogramSvg}
        style={{ maxWidth: '100%' }}
      />
      {onTickClick && (
        <div
          className="mode-buttons"
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            display: 'flex',
            gap: '2px',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '4px',
            padding: '2px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            opacity: '0',
            transition: 'opacity 0.2s'
          }}>
          <button
            onClick={() => setMode('pan')}
            title="Pan mode"
            style={{
              width: '28px',
              height: '28px',
              padding: '0',
              border: 'none',
              borderRadius: '4px',
              background: mode === 'pan' ? 'rgba(66, 133, 244, 0.2)' : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (mode !== 'pan') {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (mode !== 'pan') {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1L6 3h1.5v4.5H3V6L1 8l2 2v-1.5h4.5V13H6l2 2 2-2H8.5V8.5H13V10l2-2-2-2v1.5H8.5V3H10L8 1z"
                fill={mode === 'pan' ? COLORS.primary.blue : COLORS.background.mutedText} />
            </svg>
          </button>
          <button
            onClick={() => setMode('select')}
            title="Selection mode"
            style={{
              width: '28px',
              height: '28px',
              padding: '0',
              border: 'none',
              borderRadius: '4px',
              background: mode === 'select' ? 'rgba(66, 133, 244, 0.2)' : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (mode !== 'select') {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (mode !== 'select') {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="12" height="12" rx="1"
                stroke={mode === 'select' ? COLORS.primary.blue : COLORS.background.mutedText}
                strokeWidth="1.5"
                strokeDasharray="3 2"
                fill="none" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
